import { BUILD_ID } from '../version';

export const WEBSITE_VERSION_KEY = 'xiaoying:website-version';
export const WEBSITE_RELOAD_PARAM = '_xy_version';

export function rememberWebsiteVersion(buildId) {
  try { localStorage.setItem(WEBSITE_VERSION_KEY, buildId); } catch { /* Storage may be unavailable. */ }
}

export function hasWebsiteUpdate(buildId) {
  try {
    const previous = localStorage.getItem(WEBSITE_VERSION_KEY);
    // Establish a baseline for first-time visitors; only returning visitors see an update.
    if (!previous) { rememberWebsiteVersion(buildId); return false; }
    return previous !== buildId;
  } catch { return false; }
}

export function websiteReloadTarget(target, marker) {
  const url = new URL(target, window.location.origin);
  url.searchParams.set(WEBSITE_RELOAD_PARAM, marker);
  return `${url.pathname}${url.search}${url.hash}`;
}

export function clearWebsiteReloadMarker() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(WEBSITE_RELOAD_PARAM)) return;
  url.searchParams.delete(WEBSITE_RELOAD_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

// Only called before mounting the workspace. Never reload an active editor in the background.
export async function checkWebsiteVersion({ signal, buildId = BUILD_ID, reload = url => window.location.replace(url) } = {}) {
  if (!buildId) return 'development';
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store', signal });
    if (!response.ok) return 'unavailable';
    const latest = await response.json();
    if (signal?.aborted) return 'unavailable';
    if (typeof latest?.buildId !== 'string' || !/^[a-zA-Z0-9._-]{1,128}$/.test(latest.buildId)
        || typeof latest.version !== 'string' || !Number.isFinite(Date.parse(latest.builtAt))) return 'unavailable';
    if (latest.buildId === buildId) return 'current';
    // One automatic retry per navigation, including when caches or a release are inconsistent.
    if (new URL(window.location.href).searchParams.has(WEBSITE_RELOAD_PARAM)) return 'outdated';
    reload(websiteReloadTarget(window.location.href, latest.buildId));
    return 'reloading';
  } catch { return 'unavailable'; }
}

export function reloadWebsiteAfterLogin(target, buildId = BUILD_ID, reload = url => window.location.replace(url)) {
  if (!buildId) return false;
  // A fresh document also refreshes code when the login page was open during a deployment.
  reload(websiteReloadTarget(target, `login-${Date.now()}`));
  return true;
}
